/**
 * OAuth Configuration Composable
 *
 * Provides centralized OAuth configuration with dynamic redirect URI construction.
 * Redirect URIs are built from APP_BASE_URL environment variable, making the app
 * portable across different environments (dev, staging, production).
 *
 * Usage:
 * ```typescript
 * const oauth = useOAuthConfig()
 *
 * // Get full config for a service
 * const { clientId, redirectUri, scope } = oauth.hubspot
 *
 * // Or just get a redirect URI
 * const redirectUri = oauth.getRedirectUri('hubspot')
 * ```
 */

export interface OAuthServiceConfig {
  clientId: string;
  redirectUri: string;
  scope?: string;
  authorizeUrl: string;
}

export const useOAuthConfig = () => {
  const config = useRuntimeConfig();
  let appBaseUrl = config.public.appBaseUrl;

  // For Amplify preview branches (feature branches), detect the URL dynamically
  // Amplify sets the hostname in the browser, but APP_BASE_URL might be the production URL
  if (import.meta.client && window.location.hostname.includes('amplifyapp.com')) {
    // Use the current browser URL for Amplify preview branches
    appBaseUrl = `${window.location.protocol}//${window.location.hostname}`;
  }

  /**
   * Build a redirect URI from the app base URL and a path
   * @param path - The callback path (e.g., '/integrations/hubspot/callback')
   * @returns The full redirect URI
   */
  const buildRedirectUri = (path: string): string => {
    // Remove trailing slash from base URL if present
    const base = appBaseUrl.replace(/\/$/, '');
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${cleanPath}`;
  };

  /**
   * Get redirect URI for a service
   * @param service - Service name (e.g., 'hubspot', 'salesforce')
   * @returns The redirect URI for that service
   */
  const getRedirectUri = (service: string): string => {
    return buildRedirectUri(`/integrations/${service}`);
  };

  /**
   * PropelAuth configuration
   * Note: PropelAuth uses /auth/callback instead of /integrations/{service}/callback
   */
  const propelAuth = {
    url: config.public.propelAuthUrl,
    redirectUri: buildRedirectUri('/auth/callback'),
  };

  /**
   * HubSpot CRM OAuth configuration
   */
  const hubspot: OAuthServiceConfig = {
    clientId: config.public.hubspotClientId,
    redirectUri: getRedirectUri('hubspot'),
    scope: config.public.hubspotScope,
    authorizeUrl: 'https://app.hubspot.com/oauth/authorize',
  };

  /**
   * Salesforce CRM OAuth configuration
   */
  const salesforce: OAuthServiceConfig = {
    clientId: config.public.salesforceClientId,
    redirectUri: getRedirectUri('salesforce'),
    authorizeUrl: 'https://login.salesforce.com/services/oauth2/authorize',
  };

  /**
   * Pardot (Salesforce Marketing) OAuth configuration
   * Note: Pardot uses Salesforce OAuth endpoints
   */
  const pardot: OAuthServiceConfig = {
    clientId: config.public.pardotClientId,
    redirectUri: getRedirectUri('pardot'),
    authorizeUrl: 'https://login.salesforce.com/services/oauth2/authorize',
  };

  /**
   * Slack OAuth configuration
   */
  const slack: OAuthServiceConfig = {
    clientId: config.public.slackClientId,
    redirectUri: getRedirectUri('slack'),
    scope: config.public.slackScope,
    authorizeUrl: 'https://slack.com/oauth/v2/authorize',
  };

  /**
   * Google Ads OAuth configuration
   */
  const googleAds: OAuthServiceConfig = {
    clientId: config.public.googleAdsClientId,
    redirectUri: getRedirectUri('google_ads'),
    scope: config.public.googleAdsScope,
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
  };

  /**
   * Bing Ads Microsoft OAuth configuration
   */
  const bingAds: OAuthServiceConfig = {
    clientId: config.public.bingAdsClientId as string,
    redirectUri: getRedirectUri('bing_ads'),
    scope: config.public.bingAdsScope as string | undefined,
    authorizeUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
  };

  /**
   * Bing Ads Google OAuth configuration.
   * Used when the customer signs into Microsoft Ads via Google identity.
   * access_type=offline and prompt=consent are required to receive a refresh token.
   */
  const bingAdsGoogle: OAuthServiceConfig = {
    clientId: config.public.bingAdsGoogleClientId as string,
    redirectUri: getRedirectUri('bing_ads'),
    scope: 'profile email',
    authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
  };

  /**
   * X (Twitter) Ads OAuth configuration
   */
  const xAds: OAuthServiceConfig = {
    clientId: config.public.xAdsClientId,
    redirectUri: getRedirectUri('x'),
    scope: config.public.xAdsScope,
    authorizeUrl: 'https://twitter.com/i/oauth2/authorize',
  };

  /**
   * Reddit Ads OAuth configuration
   */
  const redditAds: OAuthServiceConfig = {
    clientId: config.public.redditAdsClientId,
    redirectUri: getRedirectUri('reddit_ads'),
    scope: config.public.redditAdsScope,
    authorizeUrl: 'https://www.reddit.com/api/v1/authorize',
  };

  /**
   * Meta (Facebook) Ads OAuth configuration
   */
  const metaAds: OAuthServiceConfig = {
    clientId: config.public.metaAdsClientId,
    redirectUri: getRedirectUri('meta_ads'),
    scope: config.public.metaAdsScope,
    authorizeUrl: 'https://www.facebook.com/v18.0/dialog/oauth',
  };

  /**
   * LinkedIn Ads OAuth configuration
   */
  const linkedin: OAuthServiceConfig = {
    clientId: config.public.linkedinClientId,
    redirectUri: getRedirectUri('linkedin'),
    scope: config.public.linkedinScope,
    authorizeUrl: 'https://www.linkedin.com/oauth/v2/authorization',
  };

  /**
   * Build a complete OAuth authorization URL
   * @param service - The OAuth service configuration
   * @param additionalParams - Additional query parameters to include
   * @returns The complete authorization URL
   */
  const buildAuthUrl = (
    service: OAuthServiceConfig,
    additionalParams: Record<string, string> = {}
  ): string => {
    const params = new URLSearchParams({
      client_id: service.clientId,
      redirect_uri: service.redirectUri,
      response_type: 'code',
      ...(service.scope && {scope: service.scope}),
      ...additionalParams,
    });

    return `${service.authorizeUrl}?${params.toString()}`;
  };

  return {
    // Helper functions
    buildRedirectUri,
    getRedirectUri,
    buildAuthUrl,

    // Service configurations
    propelAuth,
    hubspot,
    salesforce,
    pardot,
    slack,
    googleAds,
    bingAds,
    bingAdsGoogle,
    xAds,
    redditAds,
    metaAds,
    linkedin,
  };
};
